import type { Contact } from "@shopify-harness/sdk";
import {
  createFileRoute,
  type ErrorComponentProps,
  Outlet,
  useRouter
} from "@tanstack/react-router";
import type { RowSelectionState } from "@tanstack/react-table";
import BanIcon from "lucide-react/dist/esm/icons/ban";
import RefreshCwIcon from "lucide-react/dist/esm/icons/refresh-cw";
import TagIcon from "lucide-react/dist/esm/icons/tag";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { DataTable } from "@/components/data-table/data-table";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { BulkActionBar, DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { FilterSearch, FilterSelect } from "@/components/data-table/filter-controls";
import { useDensity } from "@/components/data-table/use-density";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states/state-views";
import { Button } from "@/components/ui/button";
import { contactName, useContactTable } from "@/features/contacts/contact-table-columns";
import { contactQueryKeys, contactsQueryOptions } from "@/features/contacts/queries";
import { type ContactsSearch, contactsSearchSchema } from "@/features/contacts/schemas";
import { formatInteger } from "@/lib/format";

export const Route = createFileRoute("/contacts")({
  validateSearch: contactsSearchSchema,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(contactsQueryOptions(context.api.contacts)),
  pendingComponent: ContactsLoading,
  errorComponent: ContactsError,
  component: ContactsPage
});

const COLLATOR = new Intl.Collator("ja-JP");

function ContactsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const data = Route.useLoaderData();
  const { api, queryClient } = Route.useRouteContext();
  const router = useRouter();
  const { density, setDensity } = useDensity();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [syncing, setSyncing] = useState(false);

  const runLtvSync = async () => {
    setSyncing(true);
    try {
      const result = await api.contacts.syncLtv();
      toast.success(`LTVを同期しました（${result.synced}件）`, {
        description: result.truncated
          ? "顧客数が上限（25,000件）を超えたため一部のみ同期しました"
          : result.skipped > 0
            ? `メールアドレスが無い ${result.skipped}件はスキップしました`
            : undefined
      });
      await queryClient.invalidateQueries({ queryKey: contactQueryKeys.all });
      await router.invalidate();
    } catch (error) {
      toast.error("LTVを同期できませんでした", {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setSyncing(false);
    }
  };

  const update = (patch: Partial<ContactsSearch>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const filtered = useMemo(() => {
    const keyword = search.q.toLowerCase();
    const rows = data.contacts.filter((contact) => {
      if (search.status !== "all" && contact.status !== search.status) return false;
      if (!keyword) return true;
      return `${contactName(contact)} ${contact.email} ${contact.shopDomain}`
        .toLowerCase()
        .includes(keyword);
    });

    const direction = search.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => direction * compareContacts(a, b, search.sort));
  }, [data.contacts, search.q, search.status, search.sort, search.dir]);

  const pageRows = useMemo(() => {
    const start = (search.page - 1) * search.pageSize;
    return filtered.slice(start, start + search.pageSize);
  }, [filtered, search.page, search.pageSize]);

  const table = useContactTable({
    contacts: pageRows,
    search,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    onSortChange: (field, direction) => update({ sort: field, dir: direction, page: 1 })
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const isFiltered = search.q !== "" || search.status !== "all";

  const runBulk = (label: string) => {
    toast.success(`${selectedIds.length}件の顧客を${label}しました`, {
      action: { label: "元に戻す", onClick: () => toast.info("操作を取り消しました") }
    });
    setRowSelection({});
  };

  return (
    <>
      <PageHeader
        kind="data"
        title="顧客"
        description="SDK 経由で取得した顧客マスタ。絞り込み条件は URL に保存される"
        action={
          <Button type="button" size="sm" onClick={() => void runLtvSync()} disabled={syncing}>
            <RefreshCwIcon aria-hidden="true" className={syncing ? "animate-spin" : undefined} />
            {syncing ? "LTVを同期中…" : "LTVを同期"}
          </Button>
        }
      />

      <PageBody density="dense">
        <Panel
          title="顧客一覧"
          count={formatInteger(filtered.length)}
          description={
            data.cursor
              ? "現在の API は先頭 100 件を返します（続きは未取得）"
              : "全件を取得済みです"
          }
          flush
        >
          <DataTableToolbar table={table} density={density} onDensityChange={setDensity}>
            <FilterSearch
              label="名前・メール・ストア"
              value={search.q}
              onChange={(value) => update({ q: value, page: 1 })}
              placeholder="顧客を検索"
            />
            <FilterSelect
              label="配信ステータス"
              value={search.status}
              onChange={(value) => update({ status: value, page: 1 })}
              options={[
                { value: "all", label: "すべて" },
                { value: "active", label: "配信可" },
                { value: "unsubscribed", label: "配信停止" },
                { value: "suppressed", label: "配信抑止" }
              ]}
            />
          </DataTableToolbar>

          <BulkActionBar selectedCount={selectedIds.length} onClear={() => setRowSelection({})}>
            <Button type="button" size="sm" variant="outline" onClick={() => runBulk("配信停止に")}>
              <BanIcon aria-hidden="true" />
              一括で配信停止
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => runBulk("タグ付け")}>
              <TagIcon aria-hidden="true" />
              一括でタグ付け
            </Button>
          </BulkActionBar>

          <DataTable
            table={table}
            density={density}
            maxHeight="calc(100vh - 340px)"
            onRowActivate={(row) =>
              void navigate({
                to: "/contacts/$contactId",
                params: { contactId: row.original.id }
              })
            }
            emptyState={
              isFiltered ? (
                <EmptyState
                  title="条件に一致する顧客がいません"
                  description="検索語を短くするか、配信ステータスの絞り込みを解除してください。"
                  action={
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => update({ q: "", status: "all", page: 1 })}
                    >
                      絞り込みを解除
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title="顧客がまだ同期されていません"
                  description="「LTVを同期」を実行すると Shopify から顧客と累計購入金額（LTV）を取得します。"
                  action={
                    <Button size="sm" onClick={() => void runLtvSync()} disabled={syncing}>
                      {syncing ? "LTVを同期中…" : "LTVを同期"}
                    </Button>
                  }
                />
              )
            }
          />

          <DataTablePagination
            page={search.page}
            pageSize={search.pageSize}
            totalCount={filtered.length}
            onPageChange={(page) => update({ page })}
            onPageSizeChange={(pageSize) => update({ pageSize, page: 1 })}
          />
        </Panel>
      </PageBody>

      {/* 詳細は /contacts/$contactId 側でサイドパネルとして開く（一覧の文脈を失わせない） */}
      <Outlet />
    </>
  );
}

function compareContacts(a: Contact, b: Contact, field: ContactsSearch["sort"]): number {
  switch (field) {
    case "name":
      return COLLATOR.compare(contactName(a), contactName(b));
    case "shopDomain":
      return COLLATOR.compare(a.shopDomain, b.shopDomain);
    case "status":
      return COLLATOR.compare(a.status, b.status);
    case "amountSpent":
      // 未同期（null）は金額 0 より下に置く
      return (a.amountSpent ?? -1) - (b.amountSpent ?? -1);
    case "createdAt":
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  }
}

function ContactsLoading() {
  return (
    <>
      <PageHeader kind="data" title="顧客" />
      <PageBody density="dense">
        <Panel title="顧客一覧" flush>
          <TableSkeleton rows={10} columns={5} />
        </Panel>
      </PageBody>
    </>
  );
}

function ContactsError({ error, reset }: ErrorComponentProps) {
  return (
    <>
      <PageHeader kind="data" title="顧客" />
      <PageBody density="dense">
        <Panel title="顧客一覧" flush>
          <ErrorState title="顧客を読み込めませんでした" message={error.message} onRetry={reset} />
        </Panel>
      </PageBody>
    </>
  );
}
